/* ============================================================
   c_cart-gift.js — 明細単位のギフト包装
   ────────────────────────────────────────────────────────────
   契約（nokaze-app 側の取込仕様）:
     1. ギフト対象の line item に property gift_wrapping="true" を付ける
     2. 手数料商品（handle: gift-wrapping）を別 line・
        数量＝ギフト対象点数 でカートに入れる

   この 2 つがずれると「顧客は包装代を払ったのに倉庫には伝わらない」が
   無言で起きる。app 側は取込時に突合して CloudWatch へ警告を出すが、
   フロント側でもずれを作らない・作ったら直すのが本筋。

   設計:
   - 宣言的リコンシル
       操作を受けたら「あるべきカート」を丸ごと計算し、現状との差分を適用し、
       最後に必ず /cart.js を取り直して手数料数量を検算する。
       ずれていれば自動で是正する。途中失敗・別タブでの変更にも収束する。
   - 在庫の都合で「減らす操作 → 増やす操作」の順に適用する
       先に増やすと一時的に在庫を超えて add.js が弾かれる。
   - 同一 variant の line は 1 行に統合して描画している（Liquid 側）。
       そのため Dawn の数量変更・削除をこちらでフックして、
       その variant の全 line をまとめて操作する。
   - 操作は直列キューで捌き、送信中は UI をロックする。

   関連:
     snippets/c_gift-check.liquid   カート行の UI
     assets/c_checkout-guard.js     checkout ブロック理由の集約
     sections/main-cart-items.liquid / snippets/cart-drawer.liquid  行の統合描画
   ============================================================ */
(function () {
  'use strict';

  if (window.__cCartGiftInit) return;
  window.__cCartGiftInit = true;

  var GIFT_PROPERTY = 'gift_wrapping';
  // 押してから反映されるまでの待ちに直結する。
  // 1 操作あたりカート取得と更新で往復 2 回（実測で各 190ms 前後）かかるため、
  // ここを長く取ると体感がそのぶん重くなる。
  // 短くするとステッパー連打時のリクエストは増えるが、
  // その間の操作は 1 件にまとめて適用されるので実害は小さい。
  var DEBOUNCE_MS = 150;
  var RETRY_DELAY_MS = 1200;

  // 手数料商品のハンドルはテーマ設定（settings.gift_fee_handle）で差し替えられる。
  // layout/theme.liquid が window.cGiftFeeHandle に渡している。
  function feeHandle() {
    return window.cGiftFeeHandle || 'gift-wrapping';
  }

  /* ── ルーティング ──────────────────────────────────── */

  function routes() {
    var r = (window.routes || {});
    return {
      cart: r.cart_url || '/cart',
      add: r.cart_add_url || '/cart/add',
      change: r.cart_change_url || '/cart/change',
      update: r.cart_update_url || '/cart/update'
    };
  }

  /**
   * カート API のレスポンスを解釈する。
   *
   * Shopify はレート制限（429）やメンテナンス時に JSON ではなく HTML を返す。
   * res.json() を先に呼ぶと「Unexpected token '<'」という、原因の分からない
   * SyntaxError になってしまうため、本文を文字列で受けてから判断する。
   */
  function parseCartResponse(res) {
    return res.text().then(function (text) {
      var body = null;
      try {
        body = JSON.parse(text);
      } catch (e) {
        var err = new Error(
          res.status === 429
            ? 'cart request was rate limited (429)'
            : 'cart response was not JSON (status ' + res.status + ')'
        );
        err.status = res.status;
        err.rateLimited = res.status === 429;
        throw err;
      }
      if (!res.ok) {
        var apiError = new Error(body.description || body.message || 'cart error');
        apiError.status = res.status;
        apiError.rateLimited = res.status === 429;
        apiError.body = body;
        throw apiError;
      }
      return body;
    });
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * 一時的な失敗のときだけ、少し待って 1 度だけ投げ直す。
   *
   * 差分は「減らす → 増やす」の順に流している。途中で失敗すると
   * 非ギフト line を消したあとギフト line の追加だけが落ちて、
   * 商品がカートから消えることになる。レート制限やサーバー側の
   * 一時エラーはすぐ回復することが多いので、そこだけ拾う。
   * 何度も投げ直すとレート制限を悪化させるため 1 回に留める。
   */
  function requestWithRetry(makeRequest, retries) {
    return makeRequest()
      .then(parseCartResponse)
      .catch(function (err) {
        var temporary = err && (err.rateLimited || (err.status >= 500 && err.status < 600));
        if (!temporary || retries <= 0) throw err;
        return delay(RETRY_DELAY_MS).then(function () {
          return requestWithRetry(makeRequest, retries - 1);
        });
      });
  }

  function postJSON(url, data) {
    return requestWithRetry(function () {
      return fetch(url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(data)
      });
    }, 1);
  }

  function getCart() {
    return requestWithRetry(function () {
      return fetch(routes().cart + '.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
    }, 1);
  }

  /* ── カートの読み取り ──────────────────────────────── */

  function isGiftLine(line) {
    var props = line.properties || {};
    var raw = props[GIFT_PROPERTY];
    if (raw === undefined || raw === null) return false;
    return String(raw).trim().toLowerCase() === 'true';
  }

  function isFeeLine(line) {
    return line.handle === feeHandle();
  }

  /**
   * カートを variant 単位に畳む。
   * @returns {{rows: Object, fee: Object|null, giftTotal: number}}
   */
  function analyze(cart) {
    var rows = {};
    var fee = null;
    var giftTotal = 0;

    (cart.items || []).forEach(function (line) {
      if (isFeeLine(line)) {
        // 手数料 line が何らかの理由で複数あるときは先頭を正とし、残りは 0 にする
        if (fee === null) {
          fee = { key: line.key, quantity: line.quantity, variantId: line.id };
        } else {
          fee.duplicates = fee.duplicates || [];
          fee.duplicates.push(line.key);
        }
        return;
      }

      var vid = String(line.id);
      if (!rows[vid]) {
        rows[vid] = {
          variantId: line.id,
          total: 0,
          giftQty: 0,
          plainQty: 0,
          // key を 1 本だけ持つと、同じ variant に同種の line が複数あるとき
          // （他アプリが property を付けた場合など）合算した数量を 1 本に当てて
          // 数量を倍増させてしまう。全ての line を保持して充当する。
          giftLines: [],
          plainLines: []
        };
      }
      var row = rows[vid];
      row.total += line.quantity;
      if (isGiftLine(line)) {
        row.giftQty += line.quantity;
        row.giftLines.push({ key: line.key, quantity: line.quantity });
        giftTotal += line.quantity;
      } else {
        row.plainQty += line.quantity;
        row.plainLines.push({ key: line.key, quantity: line.quantity });
      }
    });

    return { rows: rows, fee: fee, giftTotal: giftTotal, cart: cart };
  }

  /* ── 目標状態の計算 ────────────────────────────────── */

  /**
   * intent を現状に当てて、その variant のあるべき (giftQty, plainQty) を返す。
   *
   * intent は「指定された項目だけ」を上書きする patch として扱う。
   * デバウンス中に数量変更とギフト変更が続けて来ても、両方を 1 件に
   * まとめて適用できるようにするため（片方を捨てると操作が黙って消える）。
   *
   *   { variantId, total: n }    合計数量を n にする
   *   { variantId, giftQty: n }  ギフト点数を n にする
   *   { variantId, giftAll: true }  その行の全点をギフトにする
   *   { variantId, remove: true }   その variant を全部消す
   *
   * giftAll は「チェックを入れた」操作。DOM に出ている数量ではなく
   * 適用時点の実数量を基準にするため、点数ではなくフラグで持つ。
   *
   * 数量変更ではギフト点数を維持し、増減は非ギフト側で吸収する。
   * 非ギフトで吸収しきれないときだけギフト点数をクランプする。
   */
  function desiredFor(row, intent) {
    if (!row) return { giftQty: 0, plainQty: 0 };
    if (intent.remove) return { giftQty: 0, plainQty: 0 };

    var total = intent.total !== undefined ? Math.max(0, intent.total) : row.total;
    if (total === 0) return { giftQty: 0, plainQty: 0 };

    var gift;
    if (intent.giftAll) {
      gift = total;
    } else if (intent.giftQty !== undefined) {
      gift = intent.giftQty;
    } else {
      gift = row.giftQty;
    }
    gift = Math.max(0, Math.min(gift, total));

    return { giftQty: gift, plainQty: total - gift };
  }

  /* ── 差分の適用 ────────────────────────────────────── */

  function changeLine(key, quantity) {
    return postJSON(routes().change, { id: key, quantity: quantity });
  }

  function addLine(variantId, quantity, properties) {
    var item = { id: variantId, quantity: quantity };
    if (properties) item.properties = properties;
    return postJSON(routes().add, { items: [item] });
  }

  /**
   * 現状 state に対して、variant ごとの目標 targets を適用する。
   * targets: { [variantId]: { giftQty, plainQty } }
   * 在庫超過を避けるため、減らす操作を全て終えてから増やす操作を流す。
   */
  /**
   * 目標状態をカートへ反映する。
   *
   * /cart/update.js は updates を配列で受け取り、カート内の line の並び順で
   * 数量を指定できる。properties 違いの line も位置で区別できるため、
   * 数量の変更はまとめて 1 本のリクエストで送れる。
   * 新規 line（properties 付き）の作成だけは add.js が要る。
   *
   * 新規追加が不要な操作では、その 1 本に sections を相乗りさせて
   * 数量更新・検算・再描画をまとめて済ませる。
   *
   * 在庫について: ギフトの ON/OFF や点数の増減では variant ごとの合計数量が
   * 変わらないため、増量と減量を同時に送っても在庫超過にはならない。
   * 合計が増えるのは数量ステッパーで増やしたときだけで、それは Shopify が
   * 在庫を見て弾く（リクエスト全体が失敗するので中途半端に適用されない）。
   *
   * @returns {Promise<{fixed: boolean, cart: Object}>}
   */
  function applyTargets(state, targets, feeVariantId) {
    var cart = state.cart;
    var desiredByKey = {};   // line key -> 目標数量
    var additions = [];      // add.js に渡す items

    /**
     * 同種の line 群に目標数量を割り当てる。
     * 先頭の line に全量を寄せ、残りは 0 にして畳む。
     * line が 1 本も無ければ新規追加する。
     *
     * 注意: 畳むと 2 本目以降の line が持っていた別の property は失われる。
     * このテーマが付ける property は gift_wrapping だけなので通常は起きないが、
     * 他アプリが同じ variant に property を付けている場合は消える。
     */
    function allocate(lines, want, makeAddition) {
      if (lines.length === 0) {
        if (want > 0) additions.push(makeAddition(want));
        return;
      }
      lines.forEach(function (line, i) {
        desiredByKey[line.key] = i === 0 ? want : 0;
      });
    }

    Object.keys(targets).forEach(function (vid) {
      var row = state.rows[vid];
      var want = targets[vid];
      if (!row) return;

      allocate(row.giftLines, want.giftQty, function (q) {
        var item = { id: row.variantId, quantity: q, properties: {} };
        item.properties[GIFT_PROPERTY] = 'true';
        return item;
      });

      allocate(row.plainLines, want.plainQty, function (q) {
        return { id: row.variantId, quantity: q };
      });
    });

    // 手数料 line。変更しない variant のギフト点数も足し込む
    var feeTarget = 0;
    Object.keys(state.rows).forEach(function (vid) {
      feeTarget += targets[vid] ? targets[vid].giftQty : state.rows[vid].giftQty;
    });

    var fee = state.fee;
    if (fee) {
      desiredByKey[fee.key] = feeTarget;
      (fee.duplicates || []).forEach(function (key) {
        desiredByKey[key] = 0;
      });
    } else if (feeTarget > 0 && feeVariantId) {
      additions.push({ id: feeVariantId, quantity: feeTarget });
    }

    // updates 配列は「カート内の line の並び順」で解釈される。
    // 直前に取得したカートを元に組み立てるため、その間に別のタブで
    // カートを変えられると位置がずれる。数百ミリ秒の窓なので通常は起きないが、
    // ずれても手数料数量の検算（checkContract / settleAndRender）で気付ける。
    var quantities = (cart.items || []).map(function (line) {
      return Object.prototype.hasOwnProperty.call(desiredByKey, line.key)
        ? desiredByKey[line.key]
        : line.quantity;
    });

    // 追加が無ければ 1 本で完結させる
    if (!additions.length) {
      var sectionTargets = sectionsToRender();
      return postJSON(routes().update, {
        updates: quantities,
        sections: sectionTargets.map(function (t) { return t.section; }),
        sections_url: window.location.pathname
      }).then(function (next) {
        applySections(next.sections || {}, sectionTargets);
        return checkContract(next, feeVariantId);
      });
    }

    // 追加があるときは、先に数量を確定させてから新しい line を作る。
    // 逆順にすると、減らす前に足すことになって在庫を超える。
    return postJSON(routes().update, { updates: quantities })
      .then(function () {
        return postJSON(routes().add, { items: additions });
      })
      .then(function () {
        return settleAndRender(feeVariantId);
      });
  }

  /**
   * 手数料 line の数量とギフト点数が合っているか見る。
   * 合っていなければ是正して描き直す。
   *
   * @returns {Promise<{fixed: boolean, cart: Object}>}
   */
  function checkContract(cart, feeVariantId) {
    var state = analyze(cart);
    var feeQty = state.fee ? state.fee.quantity : 0;
    var dupes = (state.fee && state.fee.duplicates) || [];

    if (feeQty === state.giftTotal && dupes.length === 0) {
      return Promise.resolve({ fixed: false, cart: cart });
    }
    return settleAndRender(feeVariantId).then(function (result) {
      return { fixed: true, cart: result.cart };
    });
  }

  function runSequential(ops) {
    return ops.reduce(function (chain, op) {
      return chain.then(op);
    }, Promise.resolve());
  }

  /* ── 検算 ──────────────────────────────────────────── */

  /**
   * 手数料 line の数量とギフト点数を突き合わせ、ずれていれば直す。
   * @returns {Promise<{fixed: boolean, cart: Object}>}
   */
  /**
   * 差分を当てたあとの締め処理。
   *
   * 1 本のリクエストで
   *   - 更新後のカート全体を取得（検算に使う）
   *   - 手数料 line の数量とギフト点数の突合、ずれていれば是正
   *   - セクション HTML の取得と差し替え
   * をまとめて行う。以前は検算用の GET と再描画用の POST を別々に
   * 投げていたが、update.js のレスポンスは更新後のカートそのものなので
   * 1 本で足りる。リクエストが減るぶんレート制限にも当たりにくい。
   *
   * @param {number|null} feeVariantId 手数料 variant（無ければ null）
   * @param {Object} [options] skipRender: true で描画を省く
   * @param {number} [depth] 是正後の再確認は 1 回まで
   * @returns {Promise<{fixed: boolean, cart: Object}>}
   */
  function settleAndRender(feeVariantId, options, depth) {
    var opts = options || {};
    var targets = opts.skipRender ? [] : sectionsToRender();

    return postJSON(routes().update, {
      updates: {},
      sections: targets.map(function (t) { return t.section; }),
      sections_url: window.location.pathname
    }).then(function (cart) {
      if (targets.length) {
        applySections(cart.sections || {}, targets);
      }

      var state = analyze(cart);
      var feeQty = state.fee ? state.fee.quantity : 0;
      var dupes = (state.fee && state.fee.duplicates) || [];

      if (feeQty === state.giftTotal && dupes.length === 0) {
        return { fixed: false, cart: cart };
      }

      var ops = dupes.map(function (key) {
        return changeLine.bind(null, key, 0);
      });

      if (state.fee) {
        ops.push(changeLine.bind(null, state.fee.key, state.giftTotal));
      } else if (state.giftTotal > 0 && feeVariantId) {
        ops.push(addLine.bind(null, feeVariantId, state.giftTotal, null));
      }

      // 直す手段が無い。手数料商品が非公開になった・削除された・ハンドルが
      // 変わったのに、カートには gift_wrapping の line が残っている状態。
      // ここで fixed: true を返すとチェックアウトが毎回止まり、顧客は
      // 何度押しても先へ進めなくなる。「決済できない」は「手数料がずれる」より
      // 重い障害なので通す側に倒し、突合は app 側の警告に委ねる。
      if (ops.length === 0) {
        if (window.console && console.warn) {
          console.warn('[c_cart-gift] ギフト line があるが手数料商品を解決できない');
        }
        return { fixed: false, cart: cart };
      }

      // 是正したら 1 度だけ描き直す。ここで再帰が続くと
      // 直せない状態のときにリクエストを撃ち続けることになる。
      if ((depth || 0) >= 1) {
        return { fixed: true, cart: cart };
      }

      return runSequential(ops)
        .then(function () {
          return settleAndRender(feeVariantId, options, (depth || 0) + 1);
        })
        .then(function (result) {
          return { fixed: true, cart: result.cart };
        });
    });
  }

  /* ── DOM ───────────────────────────────────────────── */

  function giftRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-c-gift]'));
  }

  function feeVariantId() {
    var el = document.querySelector('[data-c-gift-variant]');
    if (!el) return null;
    var v = parseInt(el.getAttribute('data-c-gift-variant'), 10);
    return isNaN(v) ? null : v;
  }

  function setBusy(busy) {
    giftRoots().forEach(function (el) {
      el.classList.toggle('is-busy', busy);
    });
    document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems').forEach(function (el) {
      el.classList.toggle('cart__items--disabled', busy);
    });
  }

  function toast(message) {
    var existing = document.querySelector('[data-c-gift-toast]');
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.setAttribute('data-c-gift-toast', '');
    el.className = 'c-gift-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(function () { el.classList.add('is-show'); }, 10);
    setTimeout(function () {
      el.classList.remove('is-show');
      setTimeout(function () { el.remove(); }, 300);
    }, 4000);
  }

  // Liquid の t は訳文の ' などを &#39; に変換して出すので、文字に戻す。
  // textContent にそのまま入れると「couldn&#39;t」と画面に出てしまう。
  // textarea は中身をタグとして解釈しないので、文字参照を戻すためだけに使える。
  function decodeEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    var ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }

  function strings() {
    var el = document.querySelector('[data-c-gift-strings]');
    if (!el) return {};
    var parsed;
    try {
      parsed = JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
    var out = {};
    Object.keys(parsed || {}).forEach(function (k) {
      out[k] = decodeEntities(parsed[k]);
    });
    return out;
  }

  /* ── 再描画 ────────────────────────────────────────── */

  /**
   * 再描画対象。
   *   root     … 差し替える DOM 要素の入れ物
   *   selector … その中の実際に差し替える要素（無ければ root 自身）
   *   section  … Section Rendering API に渡すセクション名
   * を持つ。DOM に無いものは対象にしないので、
   * カートページ以外でも余計なセクションを要求しない。
   *
   * section にはセクションの「ファイル名」を渡すこと。
   * Dawn の cart.js は data-id（template--<数字>__cart-items 形式）を
   * POST /cart/change.js の sections パラメータに渡しているが、
   * GET の ?sections= ではその形式は解決されず null が返る。
   * null は「取得できなかった」だけで例外にならないため、
   * 誤った名前を渡すとエラーも出さずに再描画だけが死ぬ。
   */
  function sectionsToRender() {
    var list = [];
    var onCartPage = !!document.getElementById('main-cart-items');
    // Dawn の CartDrawer は開くと animate/active が付く
    var drawerOpen = !!document.querySelector('cart-drawer.active');

    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      list.push({ section: items.dataset.id, root: items, selector: '.js-contents' });
    }

    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      list.push({ section: footer.dataset.id, root: footer, selector: '.js-contents' });
    }

    // ドロワーは theme.liquid が snippet として描画しており data-id を持たない。
    // セクションファイル自体はブロックを持たないので名前で引ける。
    // カートページを見ている間は閉じたドロワーまで描き直す必要が無い
    // （ドロワーの HTML は 15KB ほどあり、毎操作で運ぶには重い）。
    var drawer = document.getElementById('CartDrawer');
    if (drawer && (!onCartPage || drawerOpen)) {
      list.push({ section: 'cart-drawer', root: drawer, selector: '#CartDrawer' });
    }

    // ヘッダーはセクショングループの中にあるため id は
    // shopify-section-sections--<数字>__header になる。固定 id では引けないので
    // カートアイコンから親のセクションを辿る。
    var cartLink = document.querySelector('.c-header__cart');
    var headerSection = cartLink && cartLink.closest ? cartLink.closest('.shopify-section') : null;
    if (headerSection && headerSection.id) {
      list.push({
        section: headerSection.id.replace('shopify-section-', ''),
        root: headerSection,
        selector: '.shopify-section'
      });
    }

    // カートページの確認カード（混在・受注品）。
    var val = document.querySelector('[data-cart-val][data-context="cart"]');
    var valSection = val && val.closest ? val.closest('.shopify-section') : null;
    if (valSection && valSection.id) {
      list.push({
        section: valSection.id.replace('shopify-section-', ''),
        root: valSection,
        selector: '.shopify-section'
      });
    }

    return list;
  }

  /**
   * ドロワーの中身を差し替えるとオーバーレイのリスナが失われ、
   * 外側をクリックしても閉じられなくなる。
   * Dawn の CartDrawer.renderContents と同じく貼り直す。
   */
  /**
   * 再描画はサーバーの現在状態で DOM を作り直すため、まだ順番が回ってきて
   * いない行のチェックが一旦外れて見える。複数行を続けて操作したときに
   * 自分の操作が巻き戻ったように見えるので、処理待ちの意思を貼り直す。
   */
  function reapplyPendingIntents() {
    Object.keys(pendingIntent).forEach(function (key) {
      var intent = pendingIntent[key];
      var checked;
      if (intent.giftAll) {
        checked = true;
      } else if (intent.giftQty !== undefined) {
        checked = intent.giftQty > 0;
      } else {
        return;
      }
      document
        .querySelectorAll('[data-c-gift][data-c-variant="' + key + '"]')
        .forEach(function (root) {
          var box = root.querySelector('[data-c-gift-toggle]');
          if (box) box.checked = checked;
        });
    });
  }

  function rebindDrawerOverlay() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.close !== 'function') return;
    var overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    // 差し替えが空振りした回に呼ばれると同じ要素へリスナが積み増しされる。
    // 作り直された要素には印が付いていないので、それを目印にする。
    if (overlay.dataset.cGiftBound === '1') return;
    overlay.dataset.cGiftBound = '1';
    overlay.addEventListener('click', drawer.close.bind(drawer));
  }

  function innerHTMLOf(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var found = doc.querySelector(selector);
    return found ? found.innerHTML : null;
  }

  /**
   * 取得済みのセクション HTML を DOM へ流し込む。
   */
  function applySections(payload, targets) {
    targets.forEach(function (t) {
      var html = payload[t.section];
      if (!html) {
        // セクション名が解決できないと null が返る。黙って諦めると
        // 「カートは変わったのに画面が変わらない」になるので必ず残す。
        if (window.console && console.warn) {
          console.warn('[c_cart-gift] セクションを取得できなかった:', t.section);
        }
        return;
      }
      var inner = innerHTMLOf(html, t.selector);
      if (inner === null) {
        if (window.console && console.warn) {
          console.warn('[c_cart-gift] 差し替え先が見つからない:', t.section, t.selector);
        }
        return;
      }
      var el = t.root.querySelector(t.selector) || t.root;
      el.innerHTML = inner;
    });
    rebindDrawerOverlay();
    reapplyPendingIntents();
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /**
   * 単体で画面を描き直す。通常の更新は settleAndRender が兼ねるので、
   * 失敗して状態がずれたときの立て直し用。
   */
  function rerender() {
    var targets = sectionsToRender();
    if (!targets.length) return Promise.resolve();

    return postJSON(routes().update, {
      updates: {},
      sections: targets.map(function (t) { return t.section; }),
      sections_url: window.location.pathname
    }).then(function (cart) {
      applySections(cart.sections || {}, targets);
    });
  }

  /* ── 直列キュー ────────────────────────────────────── */

  var queue = Promise.resolve();

  function enqueue(task) {
    queue = queue
      .then(function () {
        setBusy(true);
        return task();
      })
      .catch(function (err) {
        var s = strings();
        if (err && err.rateLimited) {
          toast(s.rate_limited || 'The store is busy right now. Please try again shortly.');
        } else {
          toast(s.error || 'Could not update your cart. Please try again.');
        }
        if (window.console && console.warn) console.warn('[c_cart-gift]', err);

        // 失敗した時だけ描き直して、画面をカートの実状態に戻す。
        // 成功時は settleAndRender が描画まで済ませている。
        return rerender().catch(function (renderErr) {
          if (window.console && console.warn) {
            console.warn('[c_cart-gift] 再描画に失敗した', renderErr);
          }
        });
      })
      .then(function () {
        setBusy(false);
      });
    return queue;
  }

  /**
   * 溜まった intent をまとめて適用する（読み取り → 目標計算 → 差分適用 → 検算）。
   *
   * @param {Object} intents variantId をキーにした intent の集まり
   */
  function reconcile(intents) {
    var feeVid = feeVariantId();
    return getCart()
      .then(function (cart) {
        var state = analyze(cart);
        var targets = {};
        Object.keys(intents).forEach(function (key) {
          targets[key] = desiredFor(state.rows[key], intents[key]);
        });
        // applyTargets が数量更新・検算・再描画まで済ませる
        return applyTargets(state, targets, feeVid);
      });
  }

  /* ── 操作のハンドリング ────────────────────────────── */

  var flushTimer = null;
  var pendingIntent = {};   // variantId -> マージ済み intent

  /**
   * デバウンス中の操作をためて、まとめて 1 回で適用する。
   *
   * 行ごとにタイマーを持つと、3 商品を続けて操作したときに
   * reconcile が 3 回走り、そのたびにカート取得・検算・再描画が
   * 繰り返されてリクエストが掛け算になる（レート制限の主因）。
   * タイマーは 1 本にし、その間に来た操作は variant ごとに畳んで
   * まとめて適用する。
   *
   * 同じ行の中でも後勝ちで丸ごと置き換えると、
   * 「数量を 3 にしてすぐギフトにチェック」のような連続操作で
   * 先の操作が黙って消える。項目ごとに上書きする。
   */
  function scheduleIntent(patch) {
    var key = String(patch.variantId);
    var merged = pendingIntent[key] || { variantId: patch.variantId };

    if (patch.total !== undefined) merged.total = patch.total;
    if (patch.giftQty !== undefined) {
      merged.giftQty = patch.giftQty;
      delete merged.giftAll;
    }
    if (patch.giftAll) {
      merged.giftAll = true;
      delete merged.giftQty;
    }
    if (patch.remove) merged.remove = true;

    pendingIntent[key] = merged;

    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flushIntents, DEBOUNCE_MS);
  }

  function flushIntents() {
    flushTimer = null;
    var intents = pendingIntent;
    pendingIntent = {};
    if (!Object.keys(intents).length) return;
    enqueue(function () { return reconcile(intents); });
  }

  function rowOf(el) {
    return el.closest ? el.closest('[data-c-row]') : null;
  }

  /**
   * ギフト UI のイベントは capture フェーズで受け、Dawn へ伝播させない。
   *
   * Dawn の CartItems は <cart-items> で change を購読し、
   *   onChange(event) { this.updateQuantity(event.target.dataset.index, event.target.value) }
   * と処理する。ギフトのチェックボックスの change をそのまま通すと、
   * dataset.index が undefined・value が "on" のまま /cart/change を叩いて
   * 400 になり、さらに Dawn 側もカートを描き直そうとしてこちらの
   * リコンシルと競合する。
   */
  function onGiftToggle(e) {
    var input = e.target;
    if (!input.matches || !input.matches('[data-c-gift-toggle]')) return;
    e.stopPropagation();
    var root = input.closest('[data-c-gift]');
    if (!root) return;
    var vid = parseInt(root.getAttribute('data-c-variant'), 10);
    if (isNaN(vid)) return;
    // ON は「全点をギフトに」。data-c-qty は再描画前の値なので、
    // 点数ではなくフラグで渡し、適用時点の実数量を基準にする。
    if (input.checked) {
      scheduleIntent({ variantId: vid, giftAll: true });
    } else {
      scheduleIntent({ variantId: vid, giftQty: 0 });
    }
  }

  function onGiftInput(e) {
    var input = e.target;
    if (!input.matches || !input.matches('[data-c-gift-input]')) return;
    e.stopPropagation();
    var root = input.closest('[data-c-gift]');
    if (!root) return;
    var vid = parseInt(root.getAttribute('data-c-variant'), 10);
    var total = parseInt(root.getAttribute('data-c-qty'), 10) || 0;
    var n = parseInt(input.value, 10);
    if (isNaN(vid)) return;
    if (isNaN(n)) n = 0;
    n = Math.max(0, Math.min(n, total));
    input.value = n;
    scheduleIntent({ variantId: vid, giftQty: n });
  }

  function onGiftStep(e) {
    var btn = e.target.closest ? e.target.closest('[data-c-gift-step]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var root = btn.closest('[data-c-gift]');
    if (!root) return;
    var input = root.querySelector('[data-c-gift-input]');
    if (!input) return;
    var total = parseInt(root.getAttribute('data-c-qty'), 10) || 0;
    var delta = parseInt(btn.getAttribute('data-c-gift-step'), 10) || 0;
    var next = (parseInt(input.value, 10) || 0) + delta;
    next = Math.max(0, Math.min(next, total));
    input.value = next;
    var vid = parseInt(root.getAttribute('data-c-variant'), 10);
    if (isNaN(vid)) return;
    // ボタンの見た目も先に追随させる（確定は後）
    root.querySelectorAll('[data-c-gift-step]').forEach(function (b) {
      var d = parseInt(b.getAttribute('data-c-gift-step'), 10) || 0;
      b.disabled = (d < 0 && next <= 1) || (d > 0 && next >= total);
    });
    scheduleIntent({ variantId: vid, giftQty: next });
  }

  /**
   * Dawn の数量変更を横取りする。
   * 統合行の数量入力は「その variant の合計数量」を表しているため、
   * Dawn の line index ベースの更新をそのまま通すと片方の line しか動かない。
   */
  function onQuantityChange(e) {
    var input = e.target;
    if (!input.matches || !input.matches('.quantity__input')) return;
    var row = rowOf(input);
    if (!row) return;   // カート行以外（PDP など）は Dawn に任せる
    var vid = parseInt(row.getAttribute('data-c-variant'), 10);
    if (isNaN(vid)) return;

    e.preventDefault();
    e.stopPropagation();

    var total = parseInt(input.value, 10);
    if (isNaN(total) || total < 0) total = 0;
    scheduleIntent({ variantId: vid, total: total });
  }

  /**
   * Dawn の削除ボタンを横取りする。統合行はその variant の全 line を消す。
   */
  function onRemoveClick(e) {
    var btn = e.target.closest ? e.target.closest('cart-remove-button') : null;
    if (!btn) return;
    var row = rowOf(btn);
    if (!row) return;
    var vid = parseInt(row.getAttribute('data-c-variant'), 10);
    if (isNaN(vid)) return;

    e.preventDefault();
    e.stopPropagation();

    // 削除は待たせず即座に流す。まとめ待ちの操作があれば一緒に適用する。
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    var intents = pendingIntent;
    pendingIntent = {};
    intents[String(vid)] = { variantId: vid, remove: true };
    enqueue(function () { return reconcile(intents); });
  }

  /* ── チェックアウト直前の検算 ──────────────────────── */

  var checkoutVerified = false;

  var CHECKOUT_SELECTOR =
    '[name="checkout"], button[name="checkout"], .cart__checkout-button, #checkout';

  function onCheckoutAttempt(e) {
    // submit イベントの target は <form> なので submitter を見る。
    // submitter を取れない環境では click 側で捕捉される。
    var origin = e.type === 'submit' ? e.submitter : e.target;
    var btn = origin && origin.closest ? origin.closest(CHECKOUT_SELECTOR) : null;
    if (!btn) return;
    if (checkoutVerified) return;        // 検算済みならそのまま通す
    // ギフト UI の有無では判定しない。JS が途中で失敗して手数料 line だけが
    // 残ったカート（商品行ゼロ）では UI が出ないため、そこを素通りさせない。

    e.preventDefault();
    e.stopPropagation();

    setBusy(true);

    settleAndRender(feeVariantId(), { skipRender: true })
      .then(function (result) {
        if (!result.fixed) {
          // ずれていない。そのまま決済へ進む
          checkoutVerified = true;
          setBusy(false);
          // 混在カートの確認など、他の理由でブロックされているなら進めない。
          // form.submit() ではなくボタンの click を投げ直すことで、
          // name="checkout" が送られ、他機能のガードも通常どおり働く。
          if (window.CCheckoutGuard && window.CCheckoutGuard.isBlocked()) return;
          btn.click();
          return;
        }
        // 金額が変わった。一度止めて内容を確認してもらう
        var s = strings();
        return rerender()
          .catch(function () {})
          .then(function () {
            setBusy(false);
            toast(
              s.updated_notice ||
                'Gift wrapping was updated. Please review your total before checking out.'
            );
          });
      })
      .catch(function (err) {
        setBusy(false);
        var s = strings();
        if (err && err.rateLimited) {
          toast(s.rate_limited || 'The store is busy right now. Please try again shortly.');
        } else {
          toast(s.error || 'Could not update your cart. Please try again.');
        }
        if (window.console && console.warn) console.warn('[c_cart-gift]', err);
      });
  }

  /* ── 初期化 ────────────────────────────────────────── */

  function init() {
    // capture フェーズで Dawn より先に受け取る
    document.addEventListener('change', onQuantityChange, true);
    document.addEventListener('click', onRemoveClick, true);
    document.addEventListener('click', onCheckoutAttempt, true);
    document.addEventListener('submit', onCheckoutAttempt, true);

    // capture で受けて Dawn の CartItems より先に処理を奪う
    document.addEventListener('change', onGiftToggle, true);
    document.addEventListener('change', onGiftInput, true);
    document.addEventListener('click', onGiftStep, true);
  }

  // カートが変わったら「checkout 前の検算は済んでいない」に戻す
  document.addEventListener('cart:refresh', function () {
    checkoutVerified = false;
  });

  // checkout へ進んだあと戻ってきた場合（bfcache 復帰を含む）も検算し直す。
  // 別タブや戻る操作でカートが変わっている可能性がある。
  window.addEventListener('pageshow', function () {
    checkoutVerified = false;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
